import type { Dispatch, SetStateAction } from 'react';
import { cloneElement, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@eti/components';
import { Heading } from '@etg/wings';
import { css, etiColors, mediaQueries, resetButtonStyle } from '@eti/styles';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import {
  climateCompensation,
  mutuallyExclusive,
  NotAvailableCheckInBaggage,
  seatMap,
  secondBaggagePerTraveler,
} from '@eti/products';
import { analytics, constants } from '@eti/utils';
import type {
  AncillaryBundlesOnTdPageFragment,
  CheckedBaggageSummary,
  IncludedCheckedBaggage,
} from '@eti/schema-types';
import { TripCharacteristics } from '@eti/schema-types';
import { InView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';
import type { TransformedBookingStepTravelerType } from '../../../common/types/transformations/models';
import {
  flattenProducts,
  getProductLayoutType,
  isAncillaryBundleProduct,
  isCheckInBaggageNotAvailable,
} from '../utils/products';
import { pushViewItemToDataLayer } from '../../../common/data-layer/ecommerce/pushViewItemToDataLayer';
import useProductsOfferRate from '../utils/useProductsOfferRate';
import useProductsOfferRateOld from '../utils/useProductsOfferRateOld';
import { getResponsivePageViewEvent } from '../../../common/data-layer/dataLayer';
import type { AvailableExtraProductsOnTdPageFragmentType } from '../../../common/types/fragments/models';
import type { TravelerDetailsQueryTravelerPricesType } from '../../../common/types/queries/getSelectedTripOnTravelerDetailsPageQuery/models';
import type {
  ExtraProduct,
  GenerateProductComponentReturnFunctionReturnType,
} from '../../../common/utils/models';
import { routes } from '../../../constants/routesAndApisConstants';

const stepSectionStyles = (expanded: boolean) => css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${!expanded && 'display: none'};
`;

export const StepSection = ({ expanded, ...rest }: { expanded: boolean; [p: string]: any }) => (
  <div className={stepSectionStyles(expanded)} {...rest} />
);

const loadSecondaryProductsButtonStyles = css`
  align-items: center;
  color: ${etiColors.info.solid};
  display: flex;
  font-size: 0.875rem;
  margin: 0 auto 1.25rem;
  ${resetButtonStyle};
  position: relative;
  text-decoration: none;

  @media ${mediaQueries.medium.up} {
    font-size: 1rem;
    margin: 0 auto 1.875rem;
  }

  :focus {
    box-shadow: 0 0 5px rgb(53 134 209 / 50%);
    outline: 1px solid #3586d1;
  }
`;

export const getIconStyles = (isOpen: boolean) => css`
  margin-left: 0.5em;
  ${isOpen ? 'transform: rotate(180deg)' : ''};
  transition-duration: 0.4s;

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 0.5em;
  }
`;

const headingClass = (isSelfTransferGuaranteeEnabledInCart: boolean) => css`
  padding-block: ${isSelfTransferGuaranteeEnabledInCart ? '16px' : '24px 16px'};
`;

const generateKey = (index: number) => `product-section-${index}`;

const getProductPosition = (
  isOnSecondaryProductsSection: boolean,
  travelerFormProductsLength: number,
  travelers: TransformedBookingStepTravelerType[],
  index: number,
) => {
  const travelersWithoutInfants = travelers?.filter(
    ({ ageType }) => ageType !== constants.ageTypes.INFANT,
  ).length;

  return (
    (isOnSecondaryProductsSection
      ? index
      : index + travelerFormProductsLength * travelersWithoutInfants) + 1 // Start the position from one
  );
};

interface TravelerDetailsExtraProductsProps {
  ancillaryBundleSelectedValue?: string;
  bundleData: AncillaryBundlesOnTdPageFragment;
  checkedBaggageSummary: CheckedBaggageSummary;
  hasSecondaryProducts: boolean;
  includedCheckedBaggage: IncludedCheckedBaggage;
  includedProducts: GenerateProductComponentReturnFunctionReturnType[];
  isAncillaryBundleEmptyOptionSelected: boolean;
  isAncillaryBundlingEnabled: boolean;
  isSeatMapModalOpen?: boolean;
  isSeatMapSelected: boolean;
  isSectionToggled: (index: number) => boolean;
  selectionKey: string;
  products: ExtraProduct[][];
  purchasableMuxProducts: AvailableExtraProductsOnTdPageFragmentType;
  seatMapCallback?: () => void;
  setHasSecondaryProducts: Dispatch<SetStateAction<boolean>>;
  setIsSeatMapModalOpen?: Dispatch<SetStateAction<boolean>>;
  shouldSectionRender: (index: number) => boolean;
  travelerFormProducts: any[];
  travelerPrices: TravelerDetailsQueryTravelerPricesType;
  tripCharacteristics: TripCharacteristics[];
  type?: string;
  wizardSteps: number[];
  travelers: TransformedBookingStepTravelerType[];
}

const TravelerDetailsExtraProducts = ({
  ancillaryBundleSelectedValue,
  bundleData,
  checkedBaggageSummary,
  hasSecondaryProducts,
  includedCheckedBaggage,
  includedProducts,
  isAncillaryBundleEmptyOptionSelected = false,
  isAncillaryBundlingEnabled,
  isSeatMapModalOpen = false,
  isSeatMapSelected,
  isSectionToggled = () => false,
  products = [[]],
  purchasableMuxProducts,
  selectionKey,
  seatMapCallback,
  setHasSecondaryProducts,
  setIsSeatMapModalOpen,
  shouldSectionRender,
  travelers,
  travelerFormProducts,
  travelerPrices,
  tripCharacteristics,
  type,
  wizardSteps,
}: TravelerDetailsExtraProductsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { brand } = useSiteContext();
  const { p } = useProperty();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const forceSeatingModal = p('IbeClient.TravelerDetails.Modal') === 'SEATMAP';
  const isDbfEnabled = p('TravelerDetails.DynamicFlow.Enabled');
  // When DBF is enabled we do not need the primary/secondary split
  const isPrimarySecondaryEnabled =
    p('TravelerDetails.ProductsPrimarySecondary.Enabled') && !isDbfEnabled;
  const showSeatMapStandAloneProductAfterFormFilled = p(
    'TravelerDetails.ProductsPrimarySecondary.ShowSeatMapAsStandAlone.Enabled',
  );
  const isWizardEnabled =
    isPrimarySecondaryEnabled && p('TravelerDetails.ProductsPrimarySecondary.Wizard.Enabled');
  const isAncillaryCardVersionTwoEnable = p('IbeClient.AncillaryCardV2.Card.Enabled');
  const isSelfTransferGuaranteeEnabledInCart =
    p('SelfTransfer.InformationInCart.Enabled') &&
    tripCharacteristics.includes(TripCharacteristics.VirtualInterlining);
  const isOnSecondaryProductsSection = wizardSteps?.length > 2;
  const checkInBaggageNotAvailable = 'check-in-baggage-not-available';

  useEffect(() => {
    if (window?.location?.origin) {
      if (isOnSecondaryProductsSection && window?.location?.origin) {
        analytics.pushToDataLayer(
          getResponsivePageViewEvent(
            '/traveler-details-extra-services',
            'Traveler details',
            'Extra services',
            `${window.location.origin}/traveler-details-extra-services`,
          ),
        );
      }
    }
  }, [isOnSecondaryProductsSection, pathname]);

  const flattenedProducts: ExtraProduct[] = useMemo(() => flattenProducts(products), [products]);
  const showCheckInBaggageNotAvailable = isCheckInBaggageNotAvailable({
    includedCheckedBaggage,
    // @ts-ignore - WEBTECH-1361
    products: flattenedProducts,
    travelerFormProducts,
  });

  const shouldProductBeHidden = useCallback(
    ({ id, selectedWithTrip }) => {
      if (!id) {
        return true;
      }

      const isSeatMapModal =
        id === seatMap.id &&
        forceSeatingModal &&
        !(showSeatMapStandAloneProductAfterFormFilled && isSeatMapSelected);
      const isClimateCompensation = id === climateCompensation.id;

      const ancillaryBundleProduct = isAncillaryBundleProduct({
        ancillaryBundleSelectedValue,
        id,
        isAncillaryBundleEmptyOptionSelected,
        isAncillaryBundlingEnabled,
        isOnSecondaryProductsSection,
        mutuallyExclusive,
        products: flattenedProducts,
        purchasableMuxProducts,
      });

      const isSecondBagage = String(secondBaggagePerTraveler?.id) === String(id);

      return (
        isSeatMapModal ||
        selectedWithTrip ||
        isClimateCompensation ||
        ancillaryBundleProduct ||
        isSecondBagage
      );
    },
    [
      ancillaryBundleSelectedValue,
      flattenedProducts,
      forceSeatingModal,
      isAncillaryBundleEmptyOptionSelected,
      isAncillaryBundlingEnabled,
      isOnSecondaryProductsSection,
      isSeatMapSelected,
      showSeatMapStandAloneProductAfterFormFilled,
      purchasableMuxProducts,
    ],
  );
  const shouldShowImage = p('TravelerDetails.ProductImages.Enabled');

  const checkIsSectionExpanded = (index: number) =>
    isPrimarySecondaryEnabled && index === 1 ? isExpanded : true;

  const checkIsSectionHidden = (index: number) => isPrimarySecondaryEnabled && index === -1;

  const shouldShowMandatoryChoiceText =
    p('TravelerDetails.ProductSection.MandatoryChoiceText.Enabled') &&
    p('IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled');

  const extraProducts = useMemo(() => {
    const productNotAvailable = {
      component: <NotAvailableCheckInBaggage />,
      id: checkInBaggageNotAvailable,
    };
    return [
      [
        ...(showCheckInBaggageNotAvailable ? [productNotAvailable] : []),
        ...(includedProducts?.length > 0 ? includedProducts : []),
        ...(products[0] || []),
      ].filter(
        (product) =>
          !shouldProductBeHidden({
            id: 'id' in product ? product.id : undefined,
            selectedWithTrip: 'selectedWithTrip' in product ? product.selectedWithTrip : undefined,
          }),
      ),
      (products[1] || []).filter(
        (product) =>
          !shouldProductBeHidden({
            id: 'id' in product ? product.id : undefined,
            selectedWithTrip: 'selectedWithTrip' in product ? product.selectedWithTrip : undefined,
          }),
      ),
    ];
  }, [showCheckInBaggageNotAvailable, includedProducts, products, shouldProductBeHidden]);

  const isNewStoreStatisticSol = p('Offered.Product.Cache.Enabled');

  const stepIndex = pathname.includes(routes.TRAVELER_DETAILS) ? 0 : undefined;
  useProductsOfferRate(
    extraProducts,
    stepIndex,
    isOnSecondaryProductsSection,
    isNewStoreStatisticSol,
  );
  useProductsOfferRateOld(extraProducts, pathname, selectionKey, isNewStoreStatisticSol);
  useEffect(() => {
    setHasSecondaryProducts(extraProducts[1]?.length > 0);
  }, [setHasSecondaryProducts, extraProducts]);

  analytics.useLogMouseFlowTagEffect(
    'new-ancillary-card-design',
    Boolean(isAncillaryCardVersionTwoEnable),
  );

  const productPosition = (index: number) =>
    getProductPosition(
      isOnSecondaryProductsSection,
      travelerFormProducts?.length,
      travelers,
      index,
    );

  return (
    <>
      {extraProducts.map((productList, sectionIndex) => (
        <Fragment key={generateKey(sectionIndex)}>
          {isWizardEnabled && shouldSectionRender(sectionIndex + 1) && productList.length > 0 && (
            <Heading
              as="h2"
              className={headingClass(isSelfTransferGuaranteeEnabledInCart)}
              data-testid={`wizard-${sectionIndex === 0 ? 'primary' : 'secondary'}-section`}
              level={1}
            >
              {t(
                `TravelerDetails.PrimarySecondary.${
                  sectionIndex === 0 ? 'Primary' : 'Secondary'
                }.Section.Title`,
              )}
            </Heading>
          )}
          {isPrimarySecondaryEnabled &&
            !isWizardEnabled &&
            sectionIndex === 1 &&
            hasSecondaryProducts && (
              <button
                className={loadSecondaryProductsButtonStyles}
                data-testid="secondary-product-button"
                onClick={() => setIsExpanded((prev) => !prev)}
                type="button"
              >
                <span>{t('TravelerDetails.PrimarySecondary.Label')}</span>
                <Icon
                  className={getIconStyles(isExpanded)}
                  color={etiColors.info.solid}
                  id="chevron-down"
                  size="1rem"
                />
              </button>
            )}
          {((!checkIsSectionHidden(sectionIndex) &&
            checkIsSectionExpanded(sectionIndex) &&
            !isWizardEnabled) ||
            (isWizardEnabled && shouldSectionRender(sectionIndex + 1))) && (
            <StepSection
              expanded={
                isWizardEnabled
                  ? isSectionToggled(sectionIndex + 1)
                  : checkIsSectionExpanded(sectionIndex)
              }
            >
              <>
                {productList.map((product, index: number) => (
                  <InView
                    as="section"
                    className="travelerDetailsSection"
                    data-testid={`traveler-details-extra-product-${index}`}
                    key={'id' in product ? product.id : index}
                    onChange={(inView) =>
                      inView &&
                      pushViewItemToDataLayer(
                        'code' in product ? product.code : '',
                        'id' in product ? Number(product.id) : 0,
                        'component' in product ? product.component?.props?.options : [],
                      )
                    }
                    threshold={1}
                    triggerOnce
                  >
                    {'component' in product &&
                      product.component &&
                      cloneElement(product.component, {
                        productPosition: productPosition(index),
                        brand: brand.code,
                        bundleData,
                        checkedBaggageSummary,
                        isSeatMapModalOpen,
                        tripType: type,
                        layoutType: getProductLayoutType('id' in product ? product.id : '', p),
                        seatMapCallback,
                        setIsSeatMapModalOpen,
                        shouldShowImage,
                        shouldShowMandatoryChoiceText,
                        travelerLabel: t('TravelerDescription.Header'),
                        travelerPrices,
                      })}
                  </InView>
                ))}
              </>
            </StepSection>
          )}
        </Fragment>
      ))}
    </>
  );
};

export default TravelerDetailsExtraProducts;
